import React, { useEffect, useState } from "react";
import { Check, Zap, Sparkles, Building2, ArrowRight } from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import ConfirmPlanChangeModal from "./ConfirmPlanChangeModal";
import { downgradeConfirmationCopy } from "./downgradeCopy";
import { billingAPI } from "../../services/api/billing";
import { useToast } from "../../contexts/ToastContext";

// Curated, display-only plan content: plans differ on the credit allotment
// (the headline), and the ONLY extras Growth adds over Pay as you go are
// Scheduled runs, Alerts & notifications, and Co-Pilot. Intentionally does
// not enumerate raw plan.features.
const BASE_FEATURES = [
  "Observability & call transcripts",
  "LLM-powered metrics",
  "Analytics dashboards",
  "Public API access",
  "Scheduled runs",
  "Alerts & notifications",
];

const PLAN_EXTRAS = {
  pay_as_you_go: {
    base: ["Observability & call transcripts", "LLM-powered metrics", "Analytics dashboards", "Public API access"],
    extras: [],
  },
  starter:    { base: BASE_FEATURES, extras: [] },
  growth:     { base: BASE_FEATURES, extras: ["Co-Pilot"], priorTier: "Pay as you go" },
  enterprise: { base: BASE_FEATURES, extras: ["Co-Pilot", "SSO / SAML", "SLA + dedicated support"], priorTier: "Growth" },
};

function planContent(plan) {
  return PLAN_EXTRAS[plan.code] || { base: BASE_FEATURES, extras: [] };
}

function formatCredits(plan) {
  if (plan.code === "pay_as_you_go") return "Pay as you go";
  if (plan.code === "enterprise" || plan.monthly_credits === 0 || plan.monthly_price_cents == null) {
    return "Custom";
  }
  return plan.monthly_credits.toLocaleString();
}

function formatPrice(plan) {
  if (plan.monthly_price_cents == null) return { price: "Custom", unit: null };
  return { price: `$${(plan.monthly_price_cents / 100).toFixed(0)}`, unit: "/mo" };
}

function planAccent(code) {
  if (code === "enterprise") return { ring: "ring-amber-200", dot: "bg-amber-500", icon: Building2 };
  if (code === "growth")     return { ring: "ring-primary/30",   dot: "bg-primary",     icon: Sparkles };
  return                              { ring: "ring-gray-200",   dot: "bg-gray-500",    icon: Zap };
}

export default function UpgradeModal({
  open,
  onClose,
  currentCode,
  onSuccess,
  hasActiveSubscription = true,
  currentStatus,
  currentPeriodEnd,
  currentPlanName,
}) {
  const { showToast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  // Same rationale as DowngradeModal: a cheaper target picked from this same
  // plan grid (isDowngradeTarget below) must not call downgradePlan on the
  // first click — it needs the same confirm-first step.
  const [confirmingDowngradePlan, setConfirmingDowngradePlan] = useState(null);

  useEffect(() => {
    if (!open) return;
    billingAPI.listPlans().then(setPlans).catch(() => setPlans([]));
  }, [open]);

  useEffect(() => {
    if (!open) setConfirmingDowngradePlan(null);
  }, [open]);

  const currentPlan = plans.find(p => p.code === currentCode);

  // A switch to a cheaper plan is a DOWNGRADE: it must be scheduled for period end
  // (billingAPI.downgradePlan → SubscriptionSchedule), never applied immediately.
  // Routing every switch through upgradePlan (immediate, always_invoice proration)
  // was the regression that made a downgrade take effect at once and re-grant credits.
  const isDowngradeTarget = (plan) =>
    hasActiveSubscription &&
    plan.monthly_price_cents != null &&
    currentPlan?.monthly_price_cents != null &&
    plan.monthly_price_cents < currentPlan.monthly_price_cents;

  const doDowngrade = async (plan) => {
    try {
      setLoading(plan.id);
      await billingAPI.downgradePlan(plan.id);
      showToast(`Downgrade to ${plan.name} scheduled for the end of your billing period`, "success");
      setConfirmingDowngradePlan(null);
      onClose(); onSuccess?.();
    } catch (e) {
      showToast(e.response?.data?.error || "Plan change failed", "error");
    } finally {
      setLoading(null);
    }
  };

  const handle = async (plan) => {
    if (plan.code === "enterprise" || plan.is_public === false) {
      window.location.href = "mailto:noor@rubrichq.io?subject=Enterprise%20plan%20inquiry";
      return;
    }
    // A downgrade target picked from this grid — same incident class as
    // DowngradeModal's own "Schedule downgrade": never call the API on the
    // first click, only from the confirm step below.
    if (hasActiveSubscription && isDowngradeTarget(plan)) {
      setConfirmingDowngradePlan(plan);
      return;
    }
    try {
      setLoading(plan.id);
      if (hasActiveSubscription) {
        await billingAPI.upgradePlan(plan.id);
        showToast(`Switched to ${plan.name}`, "success");
        onClose(); onSuccess?.();
      } else {
        const { checkout_url } = await billingAPI.startSubscribeCheckout(plan.id);
        if (checkout_url) window.location.href = checkout_url;
        else showToast("Could not start checkout", "error");
      }
    } catch (e) {
      showToast(e.response?.data?.error || "Plan change failed", "error");
    } finally {
      setLoading(null);
    }
  };

  if (!open) return null;

  if (confirmingDowngradePlan) {
    const { title, body } = downgradeConfirmationCopy({
      currentStatus,
      currentPlanName,
      currentPeriodEnd,
      targetPlan: confirmingDowngradePlan,
    });

    return (
      <ConfirmPlanChangeModal
        open
        title={title}
        confirmLabel="Confirm downgrade"
        cancelLabel="Back"
        onCancel={() => setConfirmingDowngradePlan(null)}
        onConfirm={() => doDowngrade(confirmingDowngradePlan)}
        loading={loading === confirmingDowngradePlan.id}
      >
        <p className="text-sm text-muted-foreground">{body}</p>
      </ConfirmPlanChangeModal>
    );
  }

  return (
    <Modal title="Choose your plan" onClose={onClose} maxWidth="max-w-5xl">
      <p className="text-sm text-muted-foreground -mt-2 mb-5">
        Pick the plan that fits your needs. Switch anytime. {hasActiveSubscription ? "Upgrades apply now — you pay the plan price difference; downgrades take effect at the end of your billing period." : "You'll be redirected to Stripe to complete the signup."}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map(p => {
          const isCurrent = p.code === currentCode;
          const isEnterprise = p.code === "enterprise" || p.is_public === false;
          const accent = planAccent(p.code);
          const Icon = accent.icon;
          const { price, unit } = formatPrice(p);
          const content = planContent(p);

          return (
            <div
              key={p.id}
              className={`relative rounded-2xl border bg-card p-4 transition-all ${
                isCurrent
                  ? `border-transparent ring-2 ${accent.ring} shadow-md`
                  : "border-border hover:border-border hover:shadow-sm"
              }`}
            >
              {isCurrent && (
                <div className="absolute -top-2.5 left-5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-primary text-white uppercase tracking-wider">
                  Your plan
                </div>
              )}
              {p.code === "growth" && !isCurrent && (
                <div className="absolute -top-2.5 left-5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-wider">
                  Popular
                </div>
              )}

              {/* ── Header ── */}
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  p.code === "enterprise" ? "bg-amber-50" : p.code === "growth" ? "bg-gray-900" : "bg-muted"
                }`}>
                  <Icon className={`h-4 w-4 ${
                    p.code === "enterprise" ? "text-amber-700" : p.code === "growth" ? "text-white" : "text-muted-foreground"
                  }`} />
                </div>
                <div className="text-sm font-semibold text-foreground">{p.name}</div>
              </div>

              {/* ── Price ── */}
              <div className="mb-4">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-semibold text-foreground tracking-tight">{price}</span>
                  {unit && <span className="text-sm text-muted-foreground font-normal">{unit}</span>}
                </div>
                {isEnterprise && (
                  <div className="text-xs text-muted-foreground mt-0.5">Volume pricing · Invoice / NET 30</div>
                )}
              </div>

              {/* ── Credits highlight ── */}
              <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                      {p.code === "pay_as_you_go" ? "Billing" : "Monthly credits"}
                    </div>
                    <div className="text-lg font-semibold text-foreground mt-0.5 tabular-nums">
                      {formatCredits(p)}
                    </div>
                  </div>
                  <Zap className={`h-5 w-5 ${isEnterprise ? "text-amber-500" : "text-muted-foreground"}`} />
                </div>
              </div>

              {/* ── Features: extras (Growth = +Co-Pilot) first, then shared base ── */}
              <ul className="space-y-1.5 mb-5 min-h-[10rem]">
                {content.priorTier && content.extras.length > 0 && (
                  <li className="text-xs font-medium text-muted-foreground">
                    Everything in {content.priorTier}, plus:
                  </li>
                )}
                {content.extras.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm font-medium text-foreground">
                    <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
                {content.base.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
                {p.max_users && (
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>Up to {p.max_users} users</span>
                  </li>
                )}
              </ul>

              {/* ── Action ── */}
              {isCurrent ? (
                <button
                  disabled
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-muted-foreground bg-muted/50 cursor-not-allowed"
                >
                  Current plan
                </button>
              ) : (
                <button
                  onClick={() => handle(p)}
                  disabled={loading === p.id}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                    p.code === "growth"
                      ? "bg-gray-900 text-white hover:bg-gray-800"
                      : isEnterprise
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : "bg-card text-foreground border border-border hover:bg-muted/50"
                  } ${loading === p.id ? "opacity-60 cursor-wait" : ""}`}
                >
                  {loading === p.id
                    ? "Processing..."
                    : isEnterprise
                      ? <>Contact sales <ArrowRight className="h-3.5 w-3.5" /></>
                      : hasActiveSubscription
                        ? (isDowngradeTarget(p)
                            ? <>Schedule downgrade</>
                            : <>Switch to {p.name} <ArrowRight className="h-3.5 w-3.5" /></>)
                        : <>Start {p.name} <ArrowRight className="h-3.5 w-3.5" /></>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 pt-4 border-t border-border text-xs text-muted-foreground text-center">
        All plans include unlimited agents. Cancel anytime.
        {hasActiveSubscription ? " Secure billing handled by Stripe." : ""}
      </div>
    </Modal>
  );
}
